import { useState, useEffect } from "react";

import {
  Box,
  Grid,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert
} from "@mui/material";
import MergeIcon from "@mui/icons-material/Merge";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import useTheme from "@mui/material/styles/useTheme";

import { DefaultPaper } from "@aclymatepackages/atoms";

import { fetchOurApi } from "../../../helpers/utils/apiCalls";

const ScoreChip = ({ label, value, goodThreshold }) => {
  const { palette } = useTheme();
  const color =
    value >= goodThreshold ? palette.secondary.main : palette.warning.main;

  return (
    <Chip
      size="small"
      label={`${label}: ${Math.round(value * 100)}%`}
      style={{ backgroundColor: color, color: "white" }}
    />
  );
};

const CandidateCard = ({ candidate, onMerge, onDismiss }) => {
  const {
    id,
    vendorAId,
    vendorAName,
    vendorBId,
    vendorBName,
    fuzzyScore,
    llmConfidence,
    recommendedPrimaryId
  } = candidate;

  const { palette } = useTheme();

  const [primaryId, setPrimaryId] = useState(recommendedPrimaryId);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [dismissLoading, setDismissLoading] = useState(false);

  const secondaryId = primaryId === vendorAId ? vendorBId : vendorAId;
  const primaryName = primaryId === vendorAId ? vendorAName : vendorBName;
  const secondaryName = primaryId === vendorAId ? vendorBName : vendorAName;

  const handleMerge = async () => {
    setMergeLoading(true);
    await onMerge({
      primaryVendorId: primaryId,
      secondaryVendorId: secondaryId,
      candidateId: id
    });
    setMergeLoading(false);
  };

  const handleDismiss = async () => {
    setDismissLoading(true);
    await onDismiss(id);
    setDismissLoading(false);
  };

  return (
    <DefaultPaper>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={5}>
          <Grid container direction="column" spacing={1}>
            <Grid item>
              <Typography variant="body2" color="textSecondary">
                Primary (will be kept)
              </Typography>
            </Grid>
            <Grid item>
              <Button
                variant={primaryId === vendorAId ? "contained" : "outlined"}
                size="small"
                onClick={() => setPrimaryId(vendorAId)}
                endIcon={
                  primaryId === vendorAId ? <CheckCircleIcon /> : undefined
                }
                fullWidth
              >
                {vendorAName}
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant={primaryId === vendorBId ? "contained" : "outlined"}
                size="small"
                onClick={() => setPrimaryId(vendorBId)}
                endIcon={
                  primaryId === vendorBId ? <CheckCircleIcon /> : undefined
                }
                fullWidth
              >
                {vendorBName}
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Grid container direction="column" spacing={1} alignItems="center">
            <Grid item>
              <ScoreChip label="Fuzzy" value={fuzzyScore} goodThreshold={0.9} />
            </Grid>
            <Grid item>
              <ScoreChip
                label="LLM"
                value={llmConfidence}
                goodThreshold={0.85}
              />
            </Grid>
            <Grid item>
              <Typography
                variant="caption"
                color="textSecondary"
                align="center"
                display="block"
              >
                {primaryName} ← {secondaryName}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Grid container direction="column" spacing={1}>
            <Grid item>
              {mergeLoading ? (
                <CircularProgress size={24} />
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<MergeIcon />}
                  onClick={handleMerge}
                  fullWidth
                >
                  Merge
                </Button>
              )}
            </Grid>
            <Grid item>
              {dismissLoading ? (
                <CircularProgress size={24} />
              ) : (
                <Button
                  variant="outlined"
                  onClick={handleDismiss}
                  fullWidth
                  style={{ color: palette.text.secondary }}
                >
                  Dismiss
                </Button>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </DefaultPaper>
  );
};

const AdminVendorNormalizationPage = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOurApi({
      path: "/vendors/get-vendor-merge-candidates",
      method: "POST",
      data: {},
      callback: (data) => {
        setCandidates(data || []);
        setLoading(false);
      }
    }).catch(() => {
      setError("Failed to load merge candidates.");
      setLoading(false);
    });
  }, []);

  const onMerge = async (mergeParams) => {
    await fetchOurApi({
      path: "/vendors/merge-global-vendors",
      method: "POST",
      data: mergeParams
    });
    setCandidates((prev) =>
      prev.filter(({ id }) => id !== mergeParams.candidateId)
    );
  };

  const onDismiss = async (candidateId) => {
    await fetchOurApi({
      path: "/vendors/dismiss-vendor-merge-candidate",
      method: "POST",
      data: { candidateId }
    });
    setCandidates((prev) => prev.filter(({ id }) => id !== candidateId));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Vendor Normalization
      </Typography>
      <Typography variant="body1" color="textSecondary" gutterBottom>
        These vendor pairs were identified as likely duplicates in the global
        vendor database. Select the primary vendor to keep and merge the other
        into it.
      </Typography>
      {!candidates.length ? (
        <Box mt={4}>
          <Alert severity="success">
            No duplicate vendor candidates pending review.
          </Alert>
        </Box>
      ) : (
        <Grid container direction="column" spacing={2} mt={1}>
          {candidates.map((candidate) => (
            <Grid item key={candidate.id}>
              <CandidateCard
                candidate={candidate}
                onMerge={onMerge}
                onDismiss={onDismiss}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default AdminVendorNormalizationPage;
